import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { endOfDay, format, startOfDay } from "date-fns";
import { useHotel } from "@/hooks/useHotel";
import { requestService } from "@/services/requestService";
import { RequestType, type Request } from "@/types/request";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const PAGE_SIZE = 15;

export const useDashboardGuestRequestsInfinite = () => {
  const { hotelId } = useHotel();
  const { capabilities } = useAccessCapabilities();
  const enabled = Boolean(hotelId) && capabilities.canUseRequestsSystem;
  const ref = new Date();
  const startDate = startOfDay(ref).toISOString();
  const endDate = endOfDay(ref).toISOString();
  const dayKey = format(ref, "yyyy-MM-dd");

  const query = useInfiniteQuery({
    queryKey: [
      "dashboard",
      "guestRequests",
      "infinite",
      hotelId,
      dayKey,
      PAGE_SIZE,
    ] as const,
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const id = hotelId;
      if (!id) {
        return Promise.reject(new Error("Missing hotel"));
      }
      return requestService.getRequests({
        type: RequestType.GUEST,
        hotelId: id,
        page: pageParam,
        limit: PAGE_SIZE,
        sortField: "createdAt",
        sortDirection: "desc",
        startDate,
        endDate,
      });
    },
    getNextPageParam: (lastPage) => {
      const total = lastPage.total ?? 0;
      const limit = lastPage.limit ?? PAGE_SIZE;
      const page = lastPage.page ?? 1;
      if ((lastPage.data?.length ?? 0) === 0) {
        return undefined;
      }
      if (page * limit >= total) {
        return undefined;
      }
      return page + 1;
    },
    staleTime: 5 * 60 * 1000,
  });

  const requests = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const byId = new Map<string, Request>();
    for (const p of pages) {
      for (const r of p.data ?? []) {
        byId.set(r.id, r);
      }
    }
    return [...byId.values()];
  }, [query.data?.pages]);

  return {
    requests,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
  };
};
